import { TaskState, TaskType } from '@shared/typings/task';

import { InputParams } from '@shared/typings/models/image';
import { AbilityDependency } from './dependency';
import { isFaceSwapFilter, isVideoGen } from '../app/transform';
import { getAppCategoryName } from '@shared/hooks/useAppClassify';
import { App } from '@shared/typings/models/app';
import { IBuildFormNode } from '@shared/typings/build/classes/node';

export type IImage = Omit<GalleryApi.DTO, 'relatedTask'> & {
	relatedTask: Ability;
};

type Constructor<T = unknown> = new (...args: any[]) => T;

export type RespCode = {
	code: number;
	key: string;
	msg: string;
	msgCn: string;
};

export type ITask = {
	id: string;
	type: TaskType; // 10: 生成图片 20: 精绘 70: 节点式应用生图
	state: TaskState; // 10: 完成 20: 失败 30: 进行中 40: 排队中 0: 已删除
	startDate: string; // 任务开始时间
	priority: any;
	userId: string;
	finishDate?: string; // 任务结束时间
	createDate: string;
	currentTime: string;
	tranceId: string;
	readState: 0 | 1; // 0: 未读 1: 已读
	processTime: string;
	originProcessTime?: string;
	images: IImage[];
	traceId: string;
	respCode: RespCode | null;
	preference?: boolean;
	isHidden?: boolean;
	similarStyleRecommendList?: IGenerateSimilarStyle[];
	videoSpecs?: {
		resolution?: IBuildFormNode.VideoResolutionEnum | null;
	};
};

export type GenerateSimilarStyleData = {
	recommends: IGenerateSimilarStyle[];
	recommendAppId: string;
	abTestGroup: string;
	canGeneration: boolean;
	control: boolean;
};

export type IGenerateSimilarStyle = {
	pubId: string;
	appId: string;
	shortId: string;
	recommendAppId: string;
	name: string;
	useTotal: number;
	sampleImage: App.SampleImage;
	user: IUser;
	aiRecTraceInfo?: string;
	source?: string;
	componentFilter?: boolean; // 是否是组件筛选
};

export type EntranceInfo = {
	unReadNum: number;
	existTask: boolean;
	latestNonWaitTask: ITask;
	shieldTaskList: boolean;
};

export enum ImageFrom {
	A1 = 0,
	Faceswap = 10,
	SEGEMENT = 20,
	RECOLOR = 30,
	ENHANCE_RESOLUTION = 40,
	OPEN_API_OPTIMIZE = 50, // openApi精绘用的是50 (目前只有后端用, 前端还没有用)
	COMPOSITE_GENERATE = 60, // 合成图 (后续后成图都是这个类型, 像表情包合成图, 证件照裁剪图)
	BACKGROUND = 70, // AI背景
	BACKGROUND_SEGMENT = 71, // 换背景中间图
	DRESSUP = 31,
	AIVideo = 72, // ai video
	VIDEO_ENHANCE_RESOLUTION = 73, // 调用提升视频分辨率原子能力后的视频
}
export const sourceTypeMap2TaskType = {
	[ImageFrom.A1]: TaskType.GENERATE, //优先当成精绘
	[ImageFrom.Faceswap]: TaskType.VIDEO_SWAP_GENERATE,
	[ImageFrom.SEGEMENT]: TaskType.SEGEMENT,
	[ImageFrom.RECOLOR]: TaskType.RECOLOR,
	[ImageFrom.ENHANCE_RESOLUTION]: TaskType.ENHANCE_RESOLUTION,
	[ImageFrom.BACKGROUND]: TaskType.CHANGE_BACKGROUND,
	[ImageFrom.AIVideo]: TaskType.SIMPLE_GENERATE_VIDEO,
	[ImageFrom.VIDEO_ENHANCE_RESOLUTION]: TaskType.VIDEO_ENHANCE_RESOLUTION,
} as Record<ImageFrom, TaskType>;
/**
 * A1 能力
 */
export abstract class Ability implements ITask {
	id!: string;
	type: TaskType = 10;
	state: TaskState = 10;
	startDate!: string; // 任务开始时间
	priority: any;
	userId!: string;
	finishDate!: string; // 任务结束时间
	createDate!: string;
	currentTime!: string;
	tranceId!: string;
	readState: 0 | 1 = 0; // 0: 未读 1: 已读
	processTime!: string;
	originProcessTime?: string;
	images: IImage[] = [];
	traceId!: string;
	respCode!: RespCode | null;
	preference: boolean = false;
	existsFeedback: boolean = false;
	videoSpecs?: {
		resolution?: IBuildFormNode.VideoResolutionEnum | null;
	};

	/** 断言为视频 */
	isVideo = false; // 前端字段

	private static registry = new Map<TaskType, Constructor<Ability>>();

	constructor(task: ITask) {
		Object.assign(this, task);
	}
	static register(type: TaskType, constructor: Constructor<Ability>) {
		Ability.registry.set(type, constructor);
	}

	static from(task: ITask): Ability {
		if (task.type == TaskType.COMPOSITE_GENERATE) {
			return Object.assign(Ability.unsafe_from(task.images?.[0]), task);
		}
		const constructor = Ability.registry.get(task.type);

		if (!constructor) {
			throw new Error(`解析任务列表出现错误，找不到 type = ${task.type} 的构造函数`);
		}

		return new constructor(task);
	}

	/** 从非任务挂起的路径解析，可以仅从图片的sourceType判断任务类型, 创建出类似的结构 */
	static unsafe_from(image: GalleryApi.DTO): Ability {
		let taskType = sourceTypeMap2TaskType[image?.sourceType] || TaskType.GENERATE;
		if (!taskType) {
			throw new Error(`解析任务列表出现错误，找不到 sourceType = ${image.sourceType} 的构造函数`);
		}

		if (image?.relatedImage && taskType === TaskType.GENERATE) taskType = TaskType.OPTIMIZE;

		if (taskType === TaskType.GENERATE && image?.appInfo?.type == 'node')
			taskType = TaskType.NODE_GENERATE;

		const constructor = Ability.registry.get(taskType);
		if (!constructor) {
			throw new Error(`解析任务列表出现错误，找不到 type = ${taskType} 的构造函数`);
		}

		if (isVideoGen(image?.appInfo?.appCategory)) {
			taskType = TaskType.VIDEO_GENERATE;
		}

		if (isFaceSwapFilter(image?.appInfo?.appCategory)) {
			taskType = TaskType.FACE_SWAP_NEW;
		}
		return new constructor({
			id: image?.id,
			type: taskType,
			images: [image],
			state: TaskState.FINISH,
		});
	}
	/**
	 * 如下3个API，为兼容 PictureInfo 和 Work 两种类型
	 * 提供unsafe_from的方法从单张图片创建任务，并也支持从参数决定解析的图片
	 */

	/** 获取图片的url，有视频的话可能是视频哦 */
	abstract getUrl(image?: GalleryApi.DTO): string;

	/** 获取图片的url，必须返回图片 */
	abstract getImageUrl(image?: GalleryApi.DTO): string;

	/** 解析参数, 给卡片输入参数用 */
	abstract parseSimpleParam(image?: GalleryApi.DTO): {
		images: string[];
		texts: string[];
		videoResolution?: IBuildFormNode.VideoResolutionEnum;
		themeColor?: string;
	};

	/** 解析参数, 给web端美图详情页的输入参数用*/
	abstract parseWholeParam(image?: GalleryApi.DTO): InputParams.WholeParamItem[];

	/** 任务成功。state = 完成 && 成功 */
	get isSuccess() {
		return this.state === TaskState.FINISH;
	}
	/** 任务结束。state = 完成或者失败 */
	get isDone() {
		return this.state === TaskState.FINISH || this.state === TaskState.FAILURE;
	}
	/** 任务待定。state = 进行中或者等待中 */
	get isPending() {
		return this.state === TaskState.GOING || this.state === TaskState.WAIT;
	}

	/** 获取对应图片的AppId,即使是原子能力也有id */
	abstract get appId(): string;

	async onShare(
		location:
			| 'none'
			| 'faceswap'
			| 'invitation'
			| 'topic_detail'
			| 'profile'
			| 'post_detail_click'
			| 'post_detail_longpress'
			| 'app_card'
			| 'result_page'
			| 'image_detail'
			| 'comment'
			| 'web_fullscreen',
		img?: IImage,
	) {
		const image = img || this.images[0];
		AbilityDependency.shareImage(image.shortId, image.id, location, getAppCategoryName(img || ''));
	}
}
